/**
 * Seasons — 12-week competitive seasons with registration, standings, and champions.
 *
 * 4 seasons per year:
 *   Spring (Mar-May), Summer (Jun-Aug), Fall (Sep-Nov), Winter (Dec-Feb)
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('SEASONS');

export interface Season {
  id: string;
  name: string;
  season_number: number;
  starts_at: string;
  ends_at: string;
  status: 'upcoming' | 'active' | 'completed';
  champion_user_id: string | null;
  champion_handle: string | null;
}

export interface SeasonParticipant {
  id: string;
  season_id: string;
  user_id: string;
  registered_at: string;
  final_rank: number | null;
  final_score: number | null;
  final_dots: number | null;
}

export interface SeasonStanding {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  strength_score: number | null;
  dots_score: number | null;
  rank: number;
  registered_at: string;
}

/**
 * Fetch the current active season.
 */
export async function getActiveSeason(): Promise<Season | null> {
  const { data, error } = await supabase
    .from('seasons')
    .select('*')
    .eq('status', 'active')
    .order('season_number', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) return null;
  return data as Season;
}

/**
 * Fetch upcoming season (next to start).
 */
export async function getUpcomingSeason(): Promise<Season | null> {
  const { data } = await supabase
    .from('seasons')
    .select('*')
    .eq('status', 'upcoming')
    .order('season_number', { ascending: true })
    .limit(1)
    .single();

  return data as Season | null;
}

/**
 * Fetch all seasons.
 */
export async function getAllSeasons(): Promise<Season[]> {
  const { data } = await supabase
    .from('seasons')
    .select('*')
    .order('season_number', { ascending: false });

  return (data ?? []) as Season[];
}

/**
 * Register the current user for a season.
 */
export async function registerForSeason(
  seasonId: string,
  userId: string,
): Promise<{ success: boolean; error: string | null }> {
  const { error } = await supabase
    .from('season_participants')
    .upsert(
      { season_id: seasonId, user_id: userId },
      { onConflict: 'season_id,user_id', ignoreDuplicates: true }
    );

  if (error) {
    if (error.code === '23505') return { success: true, error: null }; // already registered
    return { success: false, error: error.message };
  }
  return { success: true, error: null };
}

/**
 * Check if user is registered for a season.
 */
export async function isRegisteredForSeason(
  seasonId: string,
  userId: string,
): Promise<boolean> {
  const { data } = await supabase
    .from('season_participants')
    .select('id')
    .eq('season_id', seasonId)
    .eq('user_id', userId)
    .single();

  return Boolean(data);
}

/**
 * Get participant count for a season.
 */
export async function getSeasonParticipantCount(seasonId: string): Promise<number> {
  const { count } = await supabase
    .from('season_participants')
    .select('id', { count: 'exact', head: true })
    .eq('season_id', seasonId);

  return count ?? 0;
}

/**
 * Get season standings (participants ranked by strength score).
 */
export async function getSeasonStandings(
  seasonId: string,
  limit = 100,
): Promise<SeasonStanding[]> {
  // Get participants
  const { data: participants } = await supabase
    .from('season_participants')
    .select('user_id, registered_at')
    .eq('season_id', seasonId);

  if (!participants?.length) return [];

  const userIds = participants.map(p => p.user_id);

  // Get profiles for these users
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, bodyweight, gender')
    .in('id', userIds);

  if (!profiles) return [];

  // Build standings sorted by strength score
  const standings: SeasonStanding[] = profiles
    .map(p => {
      const participant = participants.find(part => part.user_id === p.id);
      return {
        user_id: p.id,
        handle: p.handle,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
        strength_score: p.duel_standard_strength_kg,
        dots_score: null, // Could compute DOTS here
        rank: 0,
        registered_at: participant?.registered_at ?? '',
      };
    })
    .sort((a, b) => (b.strength_score ?? 0) - (a.strength_score ?? 0))
    .slice(0, limit)
    .map((s, i) => ({ ...s, rank: i + 1 }));

  return standings;
}

/**
 * Get season standings filtered by gym_id (for org_pro "My Gym" view).
 */
export async function getGymSeasonStandings(
  seasonId: string,
  gymId: string,
  limit = 100,
): Promise<SeasonStanding[]> {
  // Get participants
  const { data: participants } = await supabase
    .from('season_participants')
    .select('user_id, registered_at')
    .eq('season_id', seasonId);

  if (!participants?.length) return [];

  const userIds = participants.map(p => p.user_id);

  // Get profiles filtered by gym
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, bodyweight, gender, gym_id')
    .in('id', userIds)
    .eq('gym_id', gymId);

  if (!profiles) return [];

  const standings: SeasonStanding[] = profiles
    .map(p => {
      const participant = participants.find(part => part.user_id === p.id);
      return {
        user_id: p.id,
        handle: p.handle,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
        strength_score: p.duel_standard_strength_kg,
        dots_score: null,
        rank: 0,
        registered_at: participant?.registered_at ?? '',
      };
    })
    .sort((a, b) => (b.strength_score ?? 0) - (a.strength_score ?? 0))
    .slice(0, limit)
    .map((s, i) => ({ ...s, rank: i + 1 }));

  return standings;
}
